import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "contact-content" }
const _hoisted_2 = {
  id: "contact-methods",
  class: "mb-8"
}
const _hoisted_3 = {
  class: "grid gap-4 sm:gap-6 lg:grid-cols-2 xl:gap-8",
  role: "list"
}
const _hoisted_4 = ["id"]
const _hoisted_5 = ["aria-describedby"]
const _hoisted_6 = { class: "mb-3 sm:mb-4" }
const _hoisted_7 = ["href", "aria-label"]
const _hoisted_8 = { class: "text-sm sm:text-base" }
const _hoisted_9 = ["aria-label"]
const _hoisted_10 = { class: "text-gray-700" }
const _hoisted_11 = {
  id: "faq",
  class: "mb-6 sm:mb-8",
  role: "region",
  "aria-labelledby": "faq-heading"
}
const _hoisted_12 = {
  class: "space-y-4 sm:space-y-6",
  role: "list"
}
const _hoisted_13 = ["id"]
const _hoisted_14 = ["aria-labelledby"]

import { ref, onMounted } from 'vue'
import LegalPageLayout from '@/components/LegalPageLayout.vue'
import { seoManager } from '@/utils/seoUtils'

// Meta information

export default /*@__PURE__*/_defineComponent({
  __name: 'Contact',
  setup(__props) {

const lastUpdated = ref('January 15, 2025')

// Contact methods data
const contactMethods = ref([
  {
    category: 'General Support',
    email: 'collinsodeny@gmail.com',
    description: 'Questions about using our service, account issues, or general inquiries.',
    responseTime: '24-48 hours'
  },
  {
    category: 'Technical Issues',
    email: 'collinsodeny@gmail.com',
    description: 'Bug reports, technical problems, or issues with downloading wallpapers.',
    responseTime: '4-24 hours'
  },
  {
    category: 'Business Inquiries',
    email: 'collinsodeny@gmail.com',
    description: 'Partnership opportunities, advertising, or business-related questions.',
    responseTime: '2-3 business days'
  },
  {
    category: 'Privacy & Legal',
    email: 'collinsodeny@gmail.com',
    description: 'Privacy concerns, legal questions, or data protection inquiries.',
    responseTime: '1-2 business days'
  }
])

// FAQ data
const faqItems = ref([
  {
    question: 'How do I download wallpapers?',
    answer: 'Simply browse our collection, click on any wallpaper you like, and use the download button. You can choose from different resolutions to match your device.'
  },
  {
    question: 'Are the wallpapers free to use?',
    answer: 'Yes, all wallpapers on our platform are free for personal use. For commercial use, please check the specific license information or contact our business team.'
  },
  {
    question: 'Can I upload my own wallpapers?',
    answer: 'Currently, wallpaper uploads are managed by our admin team. If you\'re interested in contributing wallpapers, please contact our business team to discuss partnership opportunities.'
  },
  {
    question: 'Why isn\'t a wallpaper downloading?',
    answer: 'This could be due to browser settings, network issues, or temporary server problems. Try refreshing the page, checking your internet connection, or contact our technical support team.'
  },
  {
    question: 'How do I report inappropriate content?',
    answer: 'If you find content that violates our terms of service, please contact our support team immediately with details about the specific wallpaper and the nature of the issue.'
  }
])

// SEO Meta tags
onMounted(() => {
  // Set comprehensive SEO tags using the new SEO manager
  const metaTags = {
    title: 'Contact Us - WallpaperHub',
    description: 'Get in touch with WallpaperHub for support, technical issues, business inquiries, or privacy concerns. Multiple contact methods available with fast response times and dedicated support teams.',
    keywords: 'contact us, customer support, technical support, business inquiries, wallpaper app support, help center, contact information, support team, get in touch, customer service',
    robots: 'index, follow',
    ogType: 'website'
  };
  
  seoManager.applyMetaTags(metaTags);
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LegalPageLayout, {
    title: "Contact Us",
    lastUpdated: lastUpdated.value,
    showTableOfContents: true
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[4] || (_cache[4] = _createElementVNode("section", {
          id: "introduction",
          class: "mb-8"
        }, [
          _createElementVNode("h2", { class: "text-2xl font-semibold mb-4 text-gray-800" }, "Get in Touch"),
          _createElementVNode("p", { class: "text-gray-600 leading-relaxed mb-4" }, " We're here to help! Whether you have questions about our wallpapers, need technical support, or want to discuss business opportunities, we'd love to hear from you. Choose the most appropriate contact method below for the fastest response. ")
        ], -1)),
        _createElementVNode("section", _hoisted_2, [
          _cache[2] || (_cache[2] = _createElementVNode("h2", { class: "text-2xl font-semibold mb-6 text-gray-800" }, "Contact Methods", -1)),
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contactMethods.value, (contact) => {
              return (_openBlock(), _createElementBlock("div", {
                key: contact.category,
                class: "bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5",
                role: "listitem"
              }, [
                _createElementVNode("h3", {
                  class: "text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-gray-800",
                  id: `contact-${contact.category.toLowerCase().replace(/\s+/g, '-')}-heading`
                }, _toDisplayString(contact.category), 9, _hoisted_4),
                _createElementVNode("p", {
                  class: "text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base leading-relaxed",
                  "aria-describedby": `contact-${contact.category.toLowerCase().replace(/\s+/g, '-')}-heading`
                }, _toDisplayString(contact.description), 9, _hoisted_5),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("a", {
                    href: `mailto:${contact.email}?subject=${encodeURIComponent(contact.category + ' - Wallpaper Hub')}`,
                    class: "inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 p-2 sm:p-3 rounded-lg hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] w-full sm:w-auto break-all sm:break-normal",
                    "aria-label": `Send email to ${contact.category} at ${contact.email}`,
                    role: "link"
                  }, [
                    _cache[0] || (_cache[0] = _createElementVNode("svg", {
                      class: "w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0",
                      fill: "none",
                      stroke: "currentColor",
                      viewBox: "0 0 24 24",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "stroke-width": "2",
                        d: "M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      })
                    ], -1)),
                    _createElementVNode("span", _hoisted_8, _toDisplayString(contact.email), 1)
                  ], 8, _hoisted_7)
                ]),
                _createElementVNode("div", {
                  class: "text-xs sm:text-sm text-gray-500 bg-gray-50 p-2 sm:p-3 rounded-md",
                  role: "note",
                  "aria-label": `Expected response time for ${contact.category}: ${contact.responseTime}`
                }, [
                  _cache[1] || (_cache[1] = _createElementVNode("span", { class: "font-medium" }, "Response Time:", -1)),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(contact.responseTime), 1)
                ], 8, _hoisted_9)
              ]))
            }), 128))
          ])
        ]),
        _createElementVNode("section", _hoisted_11, [
          _cache[3] || (_cache[3] = _createElementVNode("h2", {
            id: "faq-heading",
            class: "text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-gray-800"
          }, "Frequently Asked Questions", -1)),
          _createElementVNode("div", _hoisted_12, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(faqItems.value, (faq, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: faq.question,
                class: "bg-gray-50 hover:bg-gray-100 transition-colors duration-200 rounded-lg p-4 sm:p-6",
                role: "listitem"
              }, [
                _createElementVNode("h3", {
                  class: "text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800 leading-tight",
                  id: `faq-question-${index + 1}`
                }, _toDisplayString(faq.question), 9, _hoisted_13),
                _createElementVNode("p", {
                  class: "text-gray-600 leading-relaxed text-sm sm:text-base",
                  "aria-labelledby": `faq-question-${index + 1}`,
                  role: "definition"
                }, _toDisplayString(faq.answer), 9, _hoisted_14)
              ]))
            }), 128))
          ])
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("section", {
          id: "response-expectations",
          class: "mb-6 sm:mb-8",
          role: "region",
          "aria-labelledby": "response-expectations-heading"
        }, [
          _createElementVNode("h2", {
            id: "response-expectations-heading",
            class: "text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800"
          }, "Response Expectations"),
          _createElementVNode("div", {
            class: "bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6",
            role: "note",
            "aria-label": "Response time information for different contact categories"
          }, [
            _createElementVNode("ul", {
              class: "space-y-2 sm:space-y-3 text-gray-700 text-sm sm:text-base",
              role: "list"
            }, [
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "text-blue-600 mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, [
                  _createElementVNode("strong", { class: "font-semibold" }, "General Support:"),
                  _createTextVNode(" We typically respond within 24-48 hours during business days")
                ])
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "text-blue-600 mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, [
                  _createElementVNode("strong", { class: "font-semibold" }, "Technical Issues:"),
                  _createTextVNode(" Critical issues are addressed within 4-8 hours, others within 24 hours")
                ])
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "text-blue-600 mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, [
                  _createElementVNode("strong", { class: "font-semibold" }, "Business Inquiries:"),
                  _createTextVNode(" We respond to partnership and business requests within 2-3 business days")
                ])
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "text-blue-600 mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, [
                  _createElementVNode("strong", { class: "font-semibold" }, "Legal/Privacy:"),
                  _createTextVNode(" Privacy and legal concerns are handled within 1-2 business days")
                ])
              ])
            ])
          ])
        ], -1)),
        _cache[6] || (_cache[6] = _createElementVNode("section", {
          id: "additional-info",
          class: "mb-6 sm:mb-8",
          role: "region",
          "aria-labelledby": "additional-info-heading"
        }, [
          _createElementVNode("h2", {
            id: "additional-info-heading",
            class: "text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800"
          }, "Additional Information"),
          _createElementVNode("div", { class: "space-y-4 sm:space-y-6" }, [
            _createElementVNode("p", { class: "text-gray-600 leading-relaxed text-sm sm:text-base" }, " When contacting us, please include as much relevant information as possible to help us assist you quickly and effectively: "),
            _createElementVNode("ul", {
              class: "text-gray-600 space-y-2 sm:space-y-3 ml-4 sm:ml-6 text-sm sm:text-base",
              role: "list",
              "aria-label": "Information to include when contacting support"
            }, [
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, "Your account email (if applicable)")
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, "Device and browser information (for technical issues)")
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, "Screenshots or error messages (if relevant)")
              ]),
              _createElementVNode("li", {
                class: "flex items-start",
                role: "listitem"
              }, [
                _createElementVNode("span", {
                  class: "mr-2 mt-0.5 flex-shrink-0",
                  "aria-hidden": "true"
                }, "•"),
                _createElementVNode("span", null, "Detailed description of your question or issue")
              ])
            ]),
            _createElementVNode("div", {
              class: "bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-5",
              role: "alert",
              "aria-labelledby": "urgent-issues-heading"
            }, [
              _createElementVNode("p", { class: "text-gray-700 leading-relaxed text-sm sm:text-base" }, [
                _createElementVNode("strong", {
                  id: "urgent-issues-heading",
                  class: "font-semibold text-yellow-800"
                }, "Urgent Issues:"),
                _createTextVNode(" For urgent technical issues that affect the availability of our service, please use our technical support email and mark your message as \"URGENT\" in the subject line. ")
              ])
            ])
          ])
        ], -1))
      ])
    ]),
    _: 1
  }, 8, ["lastUpdated"]))
}
}

})